"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { ClassIcon } from "@/components/icons/ClassIcon";
import { SpecIcon } from "@/components/icons/SpecIcon";
import { CLASSES, CLASS_BY_ID, SPEC_BY_ID } from "@/lib/classes";
import { useCharState, setClass, setPlayedSpec, setSelectedCharacter } from "@/lib/state";
import { useCharacters, type CharacterDTO } from "@/lib/characters";
import { classColorVar, ROLE_LABEL } from "@/lib/wow";
import { cn } from "@/lib/utils";

export function ClassSpecPicker() {
  const [open, setOpen] = useState(false);
  const state = useCharState();
  const characters = useCharacters();
  const cls = CLASS_BY_ID[state.classID] ?? CLASSES[0];
  const specEntry = SPEC_BY_ID[state.playedSpecID];
  const selected = characters.list.find((c) => c.id === state.selectedCharacterID) ?? null;

  // Trigger label: when a character is selected, show their NAME and
  // class color. Otherwise fall back to the class/spec preview label.
  const triggerName = selected?.name ?? cls.name;
  const triggerSubtitle = selected
    ? `${specEntry?.spec.name ?? ""} ${cls.name}`
    : `${specEntry?.spec.name ?? "—"} ${specEntry?.spec.role ? ROLE_LABEL[specEntry.spec.role] : ""}`;

  function pickCharacter(c: CharacterDTO) {
    if (state.classID !== c.classID) setClass(c.classID);
    setSelectedCharacter(c.id);
    setOpen(false);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-3 h-10 pl-2 pr-3 rounded-sm",
          "border border-border bg-surface hover:border-border-strong transition-colors",
          "max-w-[260px]",
        )}
      >
        <ClassIcon classToken={cls.token} size={28} />
        <div className="flex flex-col leading-tight text-left min-w-0">
          <span
            className="text-sm font-semibold truncate"
            style={{ color: classColorVar(cls.token) }}
          >
            {triggerName}
          </span>
          <span className="text-[10px] text-fg-subtle font-mono uppercase tracking-wider truncate">
            {triggerSubtitle}
          </span>
        </div>
        <ChevronDown className={cn("h-3.5 w-3.5 text-fg-subtle ml-1 transition-transform shrink-0", open && "rotate-180")} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            className={cn(
              "absolute left-0 mt-1 z-50 w-[440px] max-w-[95vw] max-h-[80vh] overflow-y-auto",
              "rounded-md border border-border-strong bg-surface shadow-[0_12px_40px_-12px_rgba(0,0,0,0.8)]",
              "p-3",
            )}
          >
            {/* Roster — only when signed in and characters loaded. */}
            {characters.list.length > 0 && (
              <>
                <p className="eyebrow mb-2">Your characters</p>
                <ul className="space-y-1">
                  {characters.list.map((c) => {
                    const charClass = CLASS_BY_ID[c.classID];
                    const accent = charClass ? classColorVar(charClass.token) : undefined;
                    const active = c.id === state.selectedCharacterID;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => pickCharacter(c)}
                          className={cn(
                            "w-full flex items-center gap-3 px-2 py-1.5 rounded-sm text-left transition-colors border-l-2",
                            active
                              ? "bg-surface-2 border-l-accent"
                              : "border-l-transparent hover:bg-surface-2/50",
                          )}
                          style={active && accent ? { borderLeftColor: accent } : undefined}
                        >
                          {charClass && <ClassIcon classToken={charClass.token} size={24} />}
                          <span className="flex-1 min-w-0">
                            <span
                              className="block text-sm font-medium truncate"
                              style={accent ? { color: accent } : undefined}
                            >
                              {c.name}
                            </span>
                            <span className="block text-[10px] text-fg-subtle font-mono uppercase tracking-wider truncate">
                              {c.realmName} ({c.region.toUpperCase()})
                            </span>
                          </span>
                          <span className="font-mono tabular text-[10px] text-fg-subtle shrink-0">
                            Lv {c.level}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <hr className="hairline my-3" />
              </>
            )}

            <p className="eyebrow mb-2">
              {characters.list.length > 0 ? "Or pick a class" : "Class"}
            </p>
            <div className="grid grid-cols-7 gap-1.5">
              {CLASSES.map((c) => {
                const active = c.id === state.classID && !selected;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setClass(c.id); }}
                    title={c.name}
                    className={cn(
                      "flex items-center justify-center p-1 rounded-xs border transition-colors",
                      active ? "border-accent bg-surface-2" : "border-transparent hover:border-border-strong hover:bg-surface-2/50",
                    )}
                  >
                    <ClassIcon classToken={c.token} size={28} />
                  </button>
                );
              })}
            </div>

            <hr className="hairline my-3" />

            <p className="eyebrow mb-2">Spec</p>
            <div className="space-y-1">
              {cls.specs.map((s) => {
                const active = s.id === state.playedSpecID;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { setPlayedSpec(s.id); setOpen(false); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-2 py-1.5 rounded-sm text-left transition-colors border-l-2",
                      active ? "bg-surface-2 border-l-accent" : "border-l-transparent hover:bg-surface-2/50",
                    )}
                  >
                    <SpecIcon specID={s.id} size={26} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm" style={{ color: classColorVar(cls.token) }}>
                        {s.name}
                      </span>
                      <span className="block text-[10px] text-fg-subtle font-mono uppercase tracking-wider">
                        {ROLE_LABEL[s.role]}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            {characters.list.length === 0 && (
              <>
                <hr className="hairline my-3" />
                <Link
                  href="/login"
                  className="block text-xs text-fg-muted hover:text-fg transition-colors text-center py-1.5"
                  onClick={() => setOpen(false)}
                >
                  Or sign in with Battle.net to import gear →
                </Link>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
